/**
 * Partners Testimonials Carousel
 * Handles scrollable testimonials carousel with pagination dots
 * Works on both mobile and desktop
 */

class PartnersTestimonialsCarousel {
    constructor(element) {
        this.container = element;
        this.testimonialsContainer = element.querySelector('.what-our-partners-say-testimonials-container');
        this.testimonialItems = element.querySelectorAll('.what-our-partners-say-card');
        this.pagination = element.querySelector('.what-our-partners-say-pagination');
        this.dots = element.querySelectorAll('.what-our-partners-say-dot');
        
        if (!this.testimonialsContainer || !this.pagination) {
            return;
        }

        this.itemsPerPage = this.getItemsPerPage();
        this.totalPages = Math.ceil(this.testimonialItems.length / this.itemsPerPage);
        this.currentPage = 0;
        this.isScrolling = false;

        this.init();
    }

    getItemsPerPage() {
        // Determine how many testimonials to show per page based on screen size
        if (window.innerWidth >= 992) {
            return 3; // Desktop: 3 testimonials
        } else if (window.innerWidth >= 768) {
            return 2; // Tablet: 2 testimonials
        } else {
            return 1; // Mobile: 1 testimonial
        }
    }

    init() {
        // Set up scroll listener
        this.testimonialsContainer.addEventListener('scroll', this.handleScroll.bind(this));
        
        // Set up pagination dot clicks
        this.dots.forEach((dot, index) => {
            dot.addEventListener('click', () => this.goToPage(index));
        });

        // Handle window resize
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                this.itemsPerPage = this.getItemsPerPage();
                this.updatePagination();
                this.updateActiveDot();
                this.togglePaginationVisibility();
            }, 250);
        });

        // Initial pagination update
        this.updatePagination();
        this.updateActiveDot();
        this.togglePaginationVisibility();
    }

    handleScroll() {
        if (this.isScrolling) return;

        this.updateActiveDot();
    }

    updateActiveDot() {
        const scrollLeft = this.testimonialsContainer.scrollLeft;
        const containerWidth = this.testimonialsContainer.offsetWidth;
        const scrollWidth = this.testimonialsContainer.scrollWidth;
        const maxScroll = scrollWidth - containerWidth;
        
        // Handle edge case when all items fit on one page
        if (maxScroll <= 0 || this.totalPages <= 1) {
            this.dots.forEach((dot, index) => {
                if (index === 0) {
                    dot.classList.add('active');
                } else {
                    dot.classList.remove('active');
                }
            });
            this.currentPage = 0;
            return;
        }
        
        // Calculate which page we're on based on scroll position
        const scrollPercentage = scrollLeft / maxScroll;
        const currentPage = Math.min(
            Math.round(scrollPercentage * (this.totalPages - 1)),
            this.totalPages - 1
        );

        // Update active dot
        this.dots.forEach((dot, index) => {
            if (index === currentPage) {
                dot.classList.add('active');
            } else {
                dot.classList.remove('active');
            }
        });

        this.currentPage = currentPage;
    }

    goToPage(pageIndex) {
        if (pageIndex < 0 || pageIndex >= this.totalPages) return;

        this.isScrolling = true;

        // Calculate scroll position
        const containerWidth = this.testimonialsContainer.offsetWidth;
        const scrollWidth = this.testimonialsContainer.scrollWidth;
        const maxScroll = scrollWidth - containerWidth;
        
        // Handle edge case when all items fit on one page
        if (maxScroll <= 0 || this.totalPages <= 1) {
            this.dots.forEach((dot, index) => {
                if (index === 0) {
                    dot.classList.add('active');
                } else {
                    dot.classList.remove('active');
                }
            });
            this.currentPage = 0;
            this.isScrolling = false;
            return;
        }
        
        // Calculate target scroll position
        const scrollPercentage = pageIndex / (this.totalPages - 1);
        const targetScroll = scrollPercentage * maxScroll;

        // Smooth scroll to target position
        this.testimonialsContainer.scrollTo({
            left: targetScroll,
            behavior: 'smooth',
        });

        // Update active dot immediately
        this.dots.forEach((dot, index) => {
            if (index === pageIndex) {
                dot.classList.add('active');
            } else {
                dot.classList.remove('active');
            }
        });

        this.currentPage = pageIndex;

        // Reset scrolling flag after animation
        setTimeout(() => {
            this.isScrolling = false;
        }, 500);
    }

    updatePagination() {
        // Recalculate total pages based on current screen size
        const newTotalPages = Math.ceil(this.testimonialItems.length / this.itemsPerPage);
        
        this.totalPages = newTotalPages;
        
        // Always update dots display based on current total pages
        this.dots.forEach((dot, index) => {
            if (index < this.totalPages) {
                dot.style.display = 'block';
            } else {
                dot.style.display = 'none';
            }
        });
        
        this.togglePaginationVisibility();
    }

    togglePaginationVisibility() {
        // Hide pagination if there's only one page or no scrolling needed
        const containerWidth = this.testimonialsContainer.offsetWidth;
        const scrollWidth = this.testimonialsContainer.scrollWidth;
        const needsScrolling = scrollWidth > containerWidth;
        
        if (this.totalPages <= 1 || !needsScrolling) {
            this.pagination.style.display = 'none';
        } else {
            this.pagination.style.display = 'flex';
        }
    }
}

// Initialize all partners testimonials carousels
function initPartnersTestimonialsCarousels() {
    const carousels = document.querySelectorAll('[data-module="partners-testimonials-carousel"]');
    carousels.forEach(carousel => {
        new PartnersTestimonialsCarousel(carousel);
    });
}

export default function init() {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPartnersTestimonialsCarousels);
    } else {
        initPartnersTestimonialsCarousels();
    }
}

